import React from "react";
import {
  ShieldCheck, AlertTriangle, FileText, Calendar, Activity,
  ClipboardCheck, Search, BarChart3, DollarSign,
} from "lucide-react";
import RichBlogLayout from "../../components/RichBlogLayout";
import {
  Prose, PullQuote, MetricsRow, BeforeAfter, ProcessFlow,
  FeatureCallout, AgentCard, ChecklistCard, StatBar,
  FlowDiagram, SectionDivider,
} from "../../components/blog/BlogGraphics";

const BlogAmandaProcedureLevelVerification = () => (
  <RichBlogLayout
    category="Insurance & Revenue Cycle"
    eyebrow="Insurance Verification"
    title="Amanda Verifies Every Patient on Every Schedule — and Flags Denial Risk Days in Advance"
    subtitle="A procedure-level breakdown for every patient, every day — written cleanly into your PMS before the chair is even sat in."
    author="Asha Team"
    date="May 2, 2026"
    readTime="8 min read"
  >
    <Prose>
      <p>
        It's Thursday afternoon. Your verification specialist is staring at
        a portal that just timed out for the third time. She has nine
        patients left for tomorrow and forty minutes before she heads home.
        Tomorrow's schedule has three crowns, two scaling-and-root-planings,
        a sealant, and one new patient whose card she hasn't even looked
        at yet.
      </p>
      <p>
        She's going to get through six of the nine. The other three will
        get a "best guess" — a basic eligibility check, a quick scan of the
        plan summary, and the silent prayer that nothing weird is hiding
        behind a frequency limit or an unmet waiting period.
      </p>
      <p>
        That weird thing — the one hiding behind the frequency limit — is
        where most denied claims and most surprise patient balances are
        born. And catching it manually, on every patient, every day, is
        almost mathematically impossible.
      </p>
    </Prose>

    <AgentCard
      name="Amanda"
      role="AI Insurance Verification · Procedure-Level Breakdowns"
      initial="A"
      description="Amanda runs procedure-level eligibility for every patient on every schedule, every day. She doesn't just confirm coverage — she pulls frequency limits, waiting periods, annual maximums, downgrade clauses, and writes a clean, structured breakdown into your PMS. And she flags denial risk days before the patient is in the chair."
      channels={["Carrier portals", "Direct eligibility", "PMS write-back", "Risk flagging"]}
    />

    <Prose>
      <h2>What manual verification actually costs</h2>
      <p>
        A strong verification specialist gets through 15–20 patients in a
        full day — and even then, the depth varies. A basic eligibility
        check is fast. A real procedure-level breakdown — frequency limits,
        waiting periods, percentages by category, downgrade rules,
        coordination of benefits — takes 15–25 minutes per patient when
        done thoroughly.
      </p>
      <p>
        A practice running two operatories per provider is easily booking
        30–60 patients a day. The math doesn't add up. So practices either
        hire more verifiers, accept denials as a cost of doing business,
        or both.
      </p>
    </Prose>

    <MetricsRow
      items={[
        { value: 100, suffix: "%", label: "Of scheduled patients verified" },
        { value: 15, prefix: "vs ", suffix: "/day", label: "Manual specialist throughput" },
        { value: 7, label: "Days of advance risk flagging" },
        { value: 0, label: "Carrier portals your team logs into" },
      ]}
    />

    <Prose>
      <p>
        Amanda doesn't make your verifier faster. She makes the volume
        constraint disappear. Every patient on every schedule, every single
        day — verified to the procedure level, written cleanly into the
        chart, with denial risks flagged before they have a chance to
        cause a denial.
      </p>

      <h2>Eligibility is the floor — Amanda goes deeper</h2>
      <p>
        Most verification tools answer "is this patient covered?" That's
        the bottom of the bar. Amanda answers the actual operating
        questions: how is this patient covered, for what, with what
        frequency, with what downgrades, against how much remaining annual
        max — and what's likely to go wrong with the claim if you proceed
        as planned.
      </p>
    </Prose>

    <BeforeAfter
      beforeTitle="Manual verification, in practice"
      afterTitle="With Amanda"
      beforeItems={[
        "15–20 patients per day, per specialist, on a good day",
        "Coverage confirmed, but frequency limits often missed",
        "Surprise balances after the visit; appeals and rework follow",
        "Half the schedule gets a 'best-guess' quick check",
      ]}
      afterItems={[
        "Every patient on every schedule, verified end to end",
        "Procedure-level breakdown — frequency, waiting, downgrades, max",
        "Denial risk flagged days in advance, before the chair is sat in",
        "All data structured into the PMS — no portal hunting later",
      ]}
    />

    <Prose>
      <h2>The full benefits pull, broken down</h2>
      <p>
        Amanda is not a single check. She is a layered set of checks that
        run against every patient on every schedule, every single day.
        Each one is the kind of thing a great human verifier would do —
        if a great human verifier could be in 60 places at once.
      </p>
    </Prose>

    <ChecklistCard
      title="Pre-visit verification · running on tomorrow's schedule"
      items={[
        { label: "Eligibility active and effective for date of service", status: "pass" },
        { label: "Annual maximum remaining ≥ planned production", status: "pass" },
        { label: "Frequency: D1110 prophy within 6-month window", status: "pass" },
        { label: "Frequency: D0274 BWX within 12-month window", status: "running" },
        { label: "Waiting period satisfied for major (D2750 crown)", status: "fail" },
        { label: "Coordination of benefits — primary vs secondary", status: "pass" },
        { label: "Downgrade rule check (composite → amalgam)", status: "pass" },
        { label: "Missing tooth clause for bridge/implant", status: "pass" },
      ]}
    />

    <Prose>
      <p>
        The failed line above — waiting period not satisfied for the
        crown — is the kind of detail that creates a denied claim three
        weeks later. Amanda flags it now, with seven days of runway, so
        the front desk can call the patient and either reschedule or
        prepare a financial plan before the visit happens.
      </p>
    </Prose>

    <ProcessFlow
      steps={[
        {
          title: "Pulls insurance information from the chart",
          body:
            "Once Morgan finishes intake, Amanda picks up the captured insurance card data — member ID, group, plan, subscriber relationship — without needing a human to retype it.",
        },
        {
          title: "Connects to the carrier",
          body:
            "Through automated portal sessions or direct eligibility exchanges, Amanda authenticates with the carrier and pulls the full benefits document.",
        },
        {
          title: "Builds a procedure-level breakdown",
          body:
            "Not just 'is the patient covered.' Amanda parses the document into category percentages, frequency limits, waiting periods, downgrades, and remaining annual max.",
        },
        {
          title: "Cross-references the day's planned production",
          body:
            "She looks at what's actually scheduled — prophy, scaling, crown, sealant — and compares each procedure against the patient's specific plan rules.",
        },
        {
          title: "Flags denial risk before it happens",
          body:
            "Anything that's likely to cause a denial — unmet waiting, exceeded frequency, missing tooth clause — gets surfaced as a risk flag with full context for the team.",
        },
        {
          title: "Writes structured data into the PMS",
          body:
            "Every benefit, every limit, every flag — written into the chart in clean, structured fields your front desk and billing team can actually use.",
        },
      ]}
    />

    <PullQuote>
      A missed frequency limit, an unmet waiting period, an annual max
      already used up — any one of these can turn into a denied claim.
      Amanda catches them days before the patient is in the chair.
    </PullQuote>

    <Prose>
      <h2>What "every patient, every day" actually looks like</h2>
      <p>
        On a normal Wednesday, the practice has 47 patients across two
        providers and four operatories. Amanda runs verification on every
        one of them overnight. The breakdown by carrier, below, is what
        she completes by 6:00 AM — before anyone on the team has logged in.
      </p>
    </Prose>

    <StatBar
      title="Tomorrow's schedule · verifications completed by carrier"
      rows={[
        { label: "Delta Dental", value: 14, max: 14 },
        { label: "Aetna", value: 9, max: 9 },
        { label: "Cigna", value: 7, max: 7 },
        { label: "BCBS", value: 6, max: 6 },
        { label: "MetLife", value: 5, max: 5 },
        { label: "United Concordia", value: 3, max: 3 },
        { label: "Other / self-pay", value: 3, max: 3 },
      ]}
    />

    <Prose>
      <h2>The downstream effect on the morning huddle</h2>
      <p>
        At 8:15 AM, the team gathers for the morning huddle. In the old
        model, the huddle is largely a list of patients with vague notes —
        "verify Linda's plan," "double-check Tom's max." With Amanda, the
        huddle is operational: here are the three risks for today, here is
        the one patient we need to call about a waiting period, here is the
        crown that's at risk of being downgraded.
      </p>
      <p>
        That single shift — from open questions to specific decisions — is
        what changes the rhythm of a morning. Nobody is verifying. Everyone
        is acting on already-verified information.
      </p>
    </Prose>

    <FlowDiagram
      title="Where verification fits in the patient journey"
      nodes={[
        { label: "Morgan completes intake", sublabel: "Insurance captured", icon: ClipboardCheck },
        { label: "Amanda pulls benefits", sublabel: "Carrier · automated", icon: Search },
        { label: "Procedure-level breakdown", sublabel: "Limits + waits + max", icon: BarChart3 },
        { label: "Risk flag → PMS", sublabel: "Front desk acts early", icon: AlertTriangle },
      ]}
    />

    <FeatureCallout
      stat="100% / day"
      label="Coverage that scales with your schedule, not your headcount"
      description="Amanda doesn't have a daily ceiling. She runs verification on every patient on every schedule — and only asks the team to step in when something genuinely needs a human call."
      icon={ShieldCheck}
    />

    <Prose>
      <h2>Where the billing team actually feels it</h2>
      <p>
        Running real verification on every patient drops the denial rate,
        cuts surprise patient balances, and shrinks rework cycles. A typical
        denial costs $25–50 in staff time to appeal, plus the cash-flow drag
        of the delay. Multiply that by the share of denials caused by
        predictable upstream misses, and the math is hard to ignore.
      </p>
      <p>
        For the patient, the experience is simpler: no surprise bill,
        no awkward call about a "rejected claim," no sense that the
        practice didn't have its act together.
      </p>
    </Prose>

    <SectionDivider label="Bottom line" />

    <Prose>
      <p>
        Verification stops being a daily scramble. Every patient on every
        schedule, broken down to the procedure, with the risky waiting periods
        and frequency conflicts surfaced seven days before the chair is sat in.
      </p>
    </Prose>
  </RichBlogLayout>
);

export default BlogAmandaProcedureLevelVerification;
